import { db } from '@/services/analytics/firebase';
import { logError } from '@personal-portfolio/common-utils';
import {
  addDoc,
  collection,
  Timestamp,
  DocumentData,
  type Firestore,
} from 'firebase/firestore';

// The only Firestore collection the browser writes to.
const RESUME_REQUESTS_COLLECTION = 'resume_requests';

export interface ResumeRequest {
  id?: string;
  userId: string;
  name: string;
  email: string;
  whatsApp?: string;
  company?: string;
  position?: string;
  message?: string;
  sendMethod: 'email' | 'whatsapp';
  timestamp: Timestamp;
  userAgent?: string;
  referrer?: string;
  currentPage?: string;
  sessionId?: string;
  status: 'pending' | 'sent' | 'failed';
  sentAt?: Timestamp;
  errorMessage?: string;
}

// Firestore service class (exported so its type can appear in declaration
// files, e.g. the useFirebase return type)
export class FirestoreService {
  private static instance: FirestoreService;

  private constructor() {
    // Intentionally empty: state lives in module-level `db`.
  }

  public static getInstance(): FirestoreService {
    if (!FirestoreService.instance) {
      FirestoreService.instance = new FirestoreService();
    }
    return FirestoreService.instance;
  }

  // Firebase may be unconfigured (missing env vars), in which case `db` is
  // null. Throwing here routes callers into their existing error handling.
  private requireDb(): Firestore {
    if (!db) {
      throw new Error('Firestore is unavailable: Firebase is not configured');
    }
    return db;
  }

  private async create<T extends DocumentData>(
    collectionName: string,
    data: Omit<T, 'id'>,
  ): Promise<string> {
    try {
      const docRef = await addDoc(
        collection(this.requireDb(), collectionName),
        {
          ...data,
          createdAt: Timestamp.now(),
        },
      );
      return docRef.id;
    } catch (error) {
      logError(`Error creating document in ${collectionName}:`, error);
      throw error;
    }
  }

  // Resume Request methods
  //
  // The browser only CREATES resume_requests — it never reads or updates
  // them. Reading/status updates happen server-side (Firebase console /
  // backend tooling), and the Firestore security rules must enforce
  // create-only client access for this collection.
  public async saveResumeRequest(
    request: Omit<ResumeRequest, 'id'>,
  ): Promise<string> {
    return this.create<ResumeRequest>(RESUME_REQUESTS_COLLECTION, request);
  }
}

// Export singleton instance
export const firestoreService = FirestoreService.getInstance();
